import { create } from 'zustand';
import type { BacktestResult, BacktestMetrics, EquityPoint, BacktestTrade, RawBacktestTrade } from '../api/restApi';
import { normalizeBacktestTrade } from '../api/restApi';

/** Normalize the trades of a (possibly raw) backtest result in place-safe
 *  fashion. The streamed `complete` event carries engine-convention trades. */
function normalizeResultTrades(result: BacktestResult): BacktestResult {
  const rawTrades = result.trades as unknown as Array<
    RawBacktestTrade & Partial<BacktestTrade>
  >;
  return { ...result, trades: rawTrades.map(normalizeBacktestTrade) };
}

// ── Streaming event types (from NDJSON) ────────────────────────

export type BacktestStreamEvent =
  | { type: 'phase'; phase: string; progress?: number; total?: number; backtestId?: string }
  | { type: 'progress'; candle: number; total: number; pct: number; time: number; backtestId?: string }
  | { type: 'candle'; time: number; open: number; high: number; low: number; close: number; volume: number; backtestId?: string }
  | { type: 'indicators'; time: number; values: Record<string, number | string | null>; backtestId?: string }
  | { type: 'trade-open'; tradeId: string; strategyId: string; symbol: string; side: string; entryPrice: number; stopLoss: number; takeProfit: number; time: number; backtestId?: string }
  | { type: 'trade-close'; tradeId: string; strategyId: string; symbol: string; side: string; entryPrice: number; exitPrice: number; realizedPnl: number; exitReason: string; time: number; backtestId?: string }
  | { type: 'equity'; points: EquityPoint[]; backtestId?: string }
  | { type: 'metrics'; metrics: Partial<BacktestMetrics>; backtestId?: string }
  | { type: 'complete'; result: BacktestResult; backtestId?: string }
  | { type: 'error'; error: string; backtestId?: string };

// ── Candle for chart ───────────────────────────────────────────

export type StreamCandle = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};

export type StreamIndicators = {
  time: number;
  values: Record<string, number | string | null>;
};

export type StreamTradeMarker = {
  tradeId: string;
  strategyId: string;
  symbol: string;
  side: string;
  price: number;
  time: number;
  action: 'open' | 'close';
  exitReason?: string;
  pnl?: number;
};

// ── Store ──────────────────────────────────────────────────────

type BacktestStatus = 'idle' | 'loading' | 'replaying' | 'complete' | 'error' | 'cancelled';

type BacktestState = {
  status: BacktestStatus;
  backtestId: string | null;
  phase: string;
  progress: { candle: number; total: number; pct: number } | null;

  // Buffered data (full stream)
  buffer: BacktestStreamEvent[];
  candles: StreamCandle[];
  indicators: StreamIndicators[];
  tradeMarkers: StreamTradeMarker[];
  equityCurve: EquityPoint[];

  // Final result
  result: BacktestResult | null;
  error: string | null;

  // Playback control (live-stream event playback — buffer-index based)
  isPlaying: boolean;
  playbackSpeed: number; // 1, 2, 5, 10, 0 = max
  playbackIndex: number; // how far into the buffer we've rendered

  // Candle-index replay scrubber (operates over the COMPLETED candle array —
  // distinct from playbackIndex which walks the live event buffer). -1 means
  // "show the full run" (no scrubbing). Set to a candle index to freeze the
  // chart/markers/equity/SL-TP at that point so the user can step through WHY
  // a strategy traded, candle by candle.
  replayIndex: number;
  replayPlaying: boolean;
  replaySpeed: number; // candles advanced per tick (1, 2, 5, 10)

  // Actions
  reset: () => void;
  setPlaying: (playing: boolean) => void;
  setSpeed: (speed: number) => void;
  setPlaybackIndex: (index: number) => void;
  pushEvent: (event: BacktestStreamEvent) => void;
  advancePlayback: () => BacktestStreamEvent | null;
  hydrateFromSavedRun: (result: BacktestResult) => void;

  // Replay-scrubber actions
  setReplayIndex: (index: number) => void;
  setReplayPlaying: (playing: boolean) => void;
  setReplaySpeed: (speed: number) => void;
  /** Advance the scrubber by `replaySpeed`, clamped to the last candle. Returns
   *  false (and pauses) when already at the end. */
  stepReplay: () => boolean;
};

export const useBacktestStore = create<BacktestState>()((set, get) => ({
  status: 'idle',
  backtestId: null,
  phase: '',
  progress: null,
  buffer: [],
  candles: [],
  indicators: [],
  tradeMarkers: [],
  equityCurve: [],
  result: null,
  error: null,
  isPlaying: true,
  playbackSpeed: 0, // max speed by default
  playbackIndex: 0,
  replayIndex: -1,
  replayPlaying: false,
  replaySpeed: 1,

  reset: () => set({
    status: 'idle',
    backtestId: null,
    phase: '',
    progress: null,
    buffer: [],
    candles: [],
    indicators: [],
    tradeMarkers: [],
    equityCurve: [],
    result: null,
    error: null,
    isPlaying: true,
    playbackIndex: 0,
    replayIndex: -1,
    replayPlaying: false,
    replaySpeed: 1,
  }),

  setPlaying: (playing) => set({ isPlaying: playing }),
  setSpeed: (speed) => set({ playbackSpeed: speed }),
  setPlaybackIndex: (index) => set({ playbackIndex: index }),

  setReplayIndex: (index) => {
    const len = get().candles.length;
    // Clamp into [-1, len-1]. -1 = "show full run".
    const clamped = index < 0 ? -1 : Math.min(index, Math.max(0, len - 1));
    set({ replayIndex: clamped });
  },
  setReplayPlaying: (playing) => set({ replayPlaying: playing }),
  setReplaySpeed: (speed) => set({ replaySpeed: speed }),
  stepReplay: () => {
    const { candles, replayIndex, replaySpeed } = get();
    const len = candles.length;
    if (len === 0) return false;
    // From "full run" (-1), a step starts the scrub at 0.
    const current = replayIndex < 0 ? -1 : replayIndex;
    const next = current + replaySpeed;
    if (next >= len - 1) {
      set({ replayIndex: len - 1, replayPlaying: false });
      return false;
    }
    set({ replayIndex: next });
    return true;
  },

  pushEvent: (event) => {
    const state = get();
    const buffer = [...state.buffer, event];
    const updates: Partial<BacktestState> = { buffer };

    if (event.type === 'phase') {
      updates.phase = event.phase;
      if (state.status === 'idle') updates.status = 'loading';
      if (event.phase === 'Running replay...') updates.status = 'replaying';
    } else if (event.type === 'progress') {
      updates.progress = { candle: event.candle, total: event.total, pct: event.pct };
    } else if (event.type === 'complete') {
      updates.status = 'complete';
      // The streamed `complete` result carries trades in the engine's raw
      // convention (openedAt/closedAt/stopLossPrice). Normalize so the
      // visualizer + saved-run hydration read consistent field names.
      updates.result = normalizeResultTrades(event.result);
    } else if (event.type === 'error') {
      updates.status = 'error';
      updates.error = event.error;
    }

    // If playing at max speed, process data events immediately
    if (state.playbackSpeed === 0 || state.isPlaying) {
      if (event.type === 'candle') {
        updates.candles = [...state.candles, event as StreamCandle];
      } else if (event.type === 'indicators') {
        updates.indicators = [...state.indicators, event as StreamIndicators];
      } else if (event.type === 'trade-open') {
        updates.tradeMarkers = [...state.tradeMarkers, {
          tradeId: event.tradeId, strategyId: event.strategyId, symbol: event.symbol,
          side: event.side, price: event.entryPrice, time: event.time, action: 'open',
        }];
      } else if (event.type === 'trade-close') {
        updates.tradeMarkers = [...state.tradeMarkers, {
          tradeId: event.tradeId, strategyId: event.strategyId, symbol: event.symbol,
          side: event.side, price: event.exitPrice, time: event.time, action: 'close',
          exitReason: event.exitReason, pnl: event.realizedPnl,
        }];
      } else if (event.type === 'equity') {
        updates.equityCurve = [...state.equityCurve, ...event.points];
      }
      updates.playbackIndex = buffer.length;
    }

    set(updates);
  },

  hydrateFromSavedRun: (result) => {
    // Rebuild trade markers from the persisted trades array. Saved runs
    // don't carry the candle/indicator stream, so the live chart stays
    // empty and we rely on the metrics + trade list + equity curve.
    const markers: StreamTradeMarker[] = [];
    for (const t of result.trades) {
      markers.push({
        tradeId: t.tradeId,
        strategyId: t.strategyId,
        symbol: t.symbol,
        side: t.side,
        price: t.entryPrice,
        time: t.entryTime,
        action: 'open',
      });
      markers.push({
        tradeId: t.tradeId,
        strategyId: t.strategyId,
        symbol: t.symbol,
        side: t.side,
        price: t.exitPrice,
        time: t.exitTime,
        action: 'close',
        exitReason: t.exitReason,
        pnl: t.realizedPnl,
      });
    }
    set({
      status: 'complete',
      backtestId: null,
      phase: 'Loaded from saved run',
      progress: null,
      buffer: [],
      candles: [],
      indicators: [],
      tradeMarkers: markers,
      equityCurve: result.equityCurve,
      result,
      error: null,
      isPlaying: false,
      playbackIndex: 0,
      replayIndex: -1,
      replayPlaying: false,
      replaySpeed: 1,
    });
  },

  advancePlayback: () => {
    const state = get();
    if (state.playbackIndex >= state.buffer.length) return null;
    const event = state.buffer[state.playbackIndex]!;
    const updates: Partial<BacktestState> = { playbackIndex: state.playbackIndex + 1 };

    if (event.type === 'candle') {
      updates.candles = [...state.candles, event as StreamCandle];
    } else if (event.type === 'indicators') {
      updates.indicators = [...state.indicators, event as StreamIndicators];
    } else if (event.type === 'trade-open') {
      updates.tradeMarkers = [...state.tradeMarkers, {
        tradeId: event.tradeId, strategyId: event.strategyId, symbol: event.symbol,
        side: event.side, price: event.entryPrice, time: event.time, action: 'open',
      }];
    } else if (event.type === 'trade-close') {
      updates.tradeMarkers = [...state.tradeMarkers, {
        tradeId: event.tradeId, strategyId: event.strategyId, symbol: event.symbol,
        side: event.side, price: event.exitPrice, time: event.time, action: 'close',
        exitReason: event.exitReason, pnl: event.realizedPnl,
      }];
    } else if (event.type === 'equity') {
      updates.equityCurve = [...state.equityCurve, ...event.points];
    }

    set(updates);
    return event;
  },
}));
